import { useEffect, useState, useRef } from "react";
import { api, formatApiError } from "../../lib/api";
import { toast } from "sonner";
import { Archive, Upload, Search, Calendar, Loader2, Clock, Trash2 } from "lucide-react";
import PlayerTimelineDialog from "../../components/PlayerTimelineDialog";

export default function SuperAdminRoosterArchive() {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [search, setSearch] = useState("");
  const [seasonFilter, setSeasonFilter] = useState("");
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [timeline, setTimeline] = useState(null);  // { fanta_id, name }
  const [deletingSeason, setDeletingSeason] = useState(null);
  const fileRef = useRef();

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get(`/superadmin/rooster-history/stats`);
      setStats(r.data);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const upload = async (e) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;
    setUploading(true);
    try {
      const fd = new FormData();
      files.forEach(f => fd.append("files", f));
      const r = await api.post(`/superadmin/rooster-history/import`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      const total = r.data.total_entries_imported;
      const seasons = (r.data.seasons_covered || []).join(", ");
      toast.success(`✅ ${total} giocatori importati. Stagioni: ${seasons}`);
      const failed = r.data.results.filter(x => !x.ok);
      if (failed.length) toast.error(`${failed.length} file falliti: ${failed[0].error}`);
      await load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setUploading(false); if (fileRef.current) fileRef.current.value = ""; }
  };

  const doSearch = async () => {
    if (search.trim().length < 2) { toast.error("Almeno 2 caratteri"); return; }
    setSearching(true);
    try {
      const params = new URLSearchParams({ q: search.trim(), limit: 50 });
      if (seasonFilter) params.set("season", seasonFilter);
      const r = await api.get(`/rooster-history/search?${params.toString()}`);
      setResults(r.data.results || []);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSearching(false); }
  };

  const deleteSeason = async (season) => {
    if (!window.confirm(`Rimuovere COMPLETAMENTE la stagione ${season} dall'archivio?\n\nOperazione irreversibile. Serve ri-upload del file XLSX per ripristinarla.`)) return;
    setDeletingSeason(season);
    try {
      const encoded = encodeURIComponent(season);
      const r = await api.delete(`/superadmin/rooster-history/${encoded}`);
      toast.success(`🗑️ Stagione ${season}: ${r.data.deleted} record rimossi`);
      await load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setDeletingSeason(null); }
  };

  return (
    <div className="p-6 max-w-6xl mx-auto space-y-6" data-testid="sa-rooster-archive-page">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <div className="text-[10px] tracking-[0.3em] text-rose-400 uppercase font-bold">SuperAdmin · Archivio Storico</div>
          <h1 className="text-3xl font-black text-white mt-1">📚 Quotazioni Fantacalcio Multi-Stagione</h1>
          <p className="text-sm text-slate-400 mt-1 max-w-2xl">
            Archivio ufficiale multi-anno delle Quotazioni Fantacalcio.it. Serve al matching di giocatori
            (Maripan, Ehizibue, Vlahovic…) che sono in rosa ma non nel listone corrente.
            Ogni admin di lega può usare l&apos;archivio via <em>Salute Rosa</em>.
          </p>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <input ref={fileRef} type="file" accept=".xlsx" multiple hidden onChange={upload} data-testid="sa-hist-upload-input"/>
          <button onClick={() => fileRef.current?.click()} disabled={uploading}
            data-testid="sa-hist-upload-btn"
            className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl bg-gradient-to-br from-rose-500 to-rose-700 text-white font-black text-sm hover:brightness-110 shadow-lg shadow-rose-500/30 disabled:opacity-40">
            {uploading ? <><Loader2 className="animate-spin" size={14}/> Caricamento…</> : <><Upload size={14}/> Carica file XLSX</>}
          </button>
        </div>
      </div>

      {/* Stats */}
      {loading ? (
        <div className="rounded-2xl border border-slate-800 bg-slate-900/40 p-8 text-center text-slate-500">Carico…</div>
      ) : stats && (
        <div className="grid sm:grid-cols-4 gap-3">
          <StatCard label="Giocatori totali" v={stats.total_entries} icon={Archive} color="rose" testid="sa-hist-total"/>
          <StatCard label="Stagioni coperte" v={stats.seasons?.length || 0} icon={Calendar} color="amber"/>
          <StatCard label="Prima stagione" v={stats.seasons?.[0]?.season || "—"} icon={Calendar} color="cyan"/>
          <StatCard label="Ultima stagione" v={stats.seasons?.[stats.seasons.length - 1]?.season || "—"} icon={Calendar} color="emerald"/>
        </div>
      )}

      {/* Per season breakdown */}
      {stats?.seasons?.length > 0 && (
        <div className="rounded-2xl border border-slate-800 bg-slate-900/40 p-4">
          <div className="text-[11px] uppercase tracking-widest text-slate-500 font-bold mb-3">Stagioni in archivio</div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-2">
            {stats.seasons.map(s => (
              <div key={s.season} data-testid={`sa-hist-season-${s.season.replace("/", "-")}`}
                className="p-3 rounded-xl border border-slate-800 bg-slate-950/60 hover:border-rose-500/40 transition-colors group relative">
                <div className="text-2xl font-black text-white font-mono">{s.season}</div>
                <div className="text-xs text-slate-400">{s.count} giocatori</div>
                <div className="text-[10px] text-slate-500 mt-1">Import: {s.last_import ? new Date(s.last_import).toLocaleString("it-IT") : "—"}</div>
                <button onClick={() => deleteSeason(s.season)} disabled={deletingSeason === s.season}
                  data-testid={`sa-hist-delete-${s.season.replace("/", "-")}`}
                  title="↩️ Rollback stagione (elimina tutti i record)"
                  className="absolute top-2 right-2 p-1.5 rounded-lg opacity-0 group-hover:opacity-100 transition-opacity bg-rose-500/20 border border-rose-500/40 text-rose-300 hover:bg-rose-500/30 disabled:opacity-40">
                  {deletingSeason === s.season ? <Loader2 className="animate-spin" size={11}/> : <Trash2 size={11}/>}
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Search */}
      <div className="rounded-2xl border border-slate-800 bg-slate-900/40 p-4">
        <div className="text-[11px] uppercase tracking-widest text-slate-500 font-bold mb-3">🔍 Ricerca in archivio</div>
        <div className="flex items-center gap-2 flex-wrap">
          <div className="flex-1 min-w-[200px] relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-600" size={14}/>
            <input value={search} onChange={e => setSearch(e.target.value)} onKeyDown={e => e.key === "Enter" && doSearch()}
              placeholder="Cerca nome giocatore (es. Vlahovic, Maripan…)"
              data-testid="sa-hist-search-input"
              className="w-full bg-slate-950 border border-slate-800 rounded-xl pl-9 pr-3 py-2 text-sm"/>
          </div>
          <select value={seasonFilter} onChange={e => setSeasonFilter(e.target.value)}
            data-testid="sa-hist-season-filter"
            className="bg-slate-950 border border-slate-800 rounded-xl px-3 py-2 text-sm">
            <option value="">Tutte le stagioni</option>
            {stats?.seasons?.map(s => (
              <option key={s.season} value={s.season}>{s.season}</option>
            ))}
          </select>
          <button onClick={doSearch} disabled={searching}
            data-testid="sa-hist-search-btn"
            className="inline-flex items-center gap-1.5 px-4 py-2 rounded-xl bg-slate-800 hover:bg-slate-700 border border-slate-700 text-sm font-bold disabled:opacity-40">
            <Search size={13}/> {searching ? "…" : "Cerca"}
          </button>
        </div>
        {results.length > 0 && (
          <div className="mt-3 max-h-96 overflow-y-auto space-y-1" data-testid="sa-hist-results">
            {results.map((r, i) => (
              <button key={`${r.season}-${r.fanta_id}-${i}`}
                onClick={() => setTimeline({ fanta_id: r.fanta_id, name: r.name })}
                data-testid={`sa-hist-result-${r.fanta_id}-${r.season.replace("/", "-")}`}
                title="Apri timeline multi-stagione"
                className="w-full text-left flex items-center gap-3 p-2 rounded-lg border border-slate-800 bg-slate-950/40 text-sm hover:border-purple-500/40 hover:bg-purple-500/5 transition-colors">
                <span className="text-[10px] font-mono font-black px-2 py-0.5 rounded bg-rose-500/15 text-rose-300 border border-rose-500/40 shrink-0">{r.season}</span>
                <span className="font-mono text-slate-500 text-xs shrink-0">#{r.fanta_id}</span>
                <span className="font-bold text-white flex-1 truncate">{r.name}</span>
                <span className="text-xs text-slate-400">{r.real_team}</span>
                <span className="text-[10px] px-1.5 py-0.5 rounded bg-slate-800 text-slate-400 font-bold">{r.role_classic}</span>
                {r.roles_mantra?.length > 0 && (
                  <span className="text-[10px] px-1.5 py-0.5 rounded bg-fuchsia-500/10 border border-fuchsia-500/30 text-fuchsia-300 font-bold">{r.roles_mantra.join("/")}</span>
                )}
                <span className="text-[10px] text-slate-500 font-mono">Q.A. {Math.round(r.qa)} · FVM {Math.round(r.fvm)}</span>
                <Clock size={12} className="text-purple-400 shrink-0"/>
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Timeline modal */}
      <PlayerTimelineDialog
        open={!!timeline}
        fantaId={timeline?.fanta_id}
        playerName={timeline?.name}
        onClose={() => setTimeline(null)}
      />
    </div>
  );
}

function StatCard({ label, v, icon: Icon, color, testid }) {
  const map = { rose: "text-rose-300 border-rose-500/40", amber: "text-amber-300 border-amber-500/40", cyan: "text-cyan-300 border-cyan-500/40", emerald: "text-emerald-300 border-emerald-500/40" };
  return (
    <div className={`rounded-2xl border ${map[color] || "border-slate-800"} bg-slate-900/60 p-4`} data-testid={testid}>
      <div className="flex items-center gap-2 text-[10px] tracking-widest uppercase text-slate-500 font-bold">
        {Icon && <Icon size={12}/>} {label}
      </div>
      <div className={`text-3xl font-black font-mono mt-1 ${map[color]?.split(" ")[0] || "text-white"}`}>{v}</div>
    </div>
  );
}
